import { useAuthStore } from '@client/stores/AuthStore';
import { cancelScheduledLoginRedirect } from '@client/utils/auth/authErrorHandler';
import { TokenRefreshManager } from '@client/utils/auth/TokenRefreshManager';

/**
 * Validity a persisted token needs for first render to proceed WITHOUT waiting on Auth0.
 *
 * This used to be `REFRESH_BUFFER_MS` (5 min), which made every hard load whose token had
 * < 5 min left block on a network refresh — `getAccessToken` defaults to `cacheMode: 'off'`,
 * so that is always a round trip to auth.curium.app, measured at P50 1.15 s on console
 * hard loads (Sentry, AU) and sitting squarely between JS boot and the first tRPC call.
 * A token that is still valid is good enough to render and fire the bootstrap queries;
 * `useAccessTokenRefresher` schedules the background refresh immediately when the token is
 * already inside the 5-minute buffer, so the renewal still happens — just off the critical
 * path. The margin covers the bootstrap requests in flight and matches auth0-spa-js's own
 * 60 s expiry adjustment; it is also the only slack against a client clock running behind
 * the server (the API verifies `exp` with no clock tolerance), so do not shrink it.
 *
 * Lives here rather than in `RequireAuth.tsx` so the boot warm-up can read it without
 * pulling the whole auth component (and its antd/router graph) into the entry chunk.
 */
export const BOOT_TOKEN_MIN_VALIDITY_MS = 60_000;

/**
 * Paths where a boot-time refresh-token exchange is safe to start.
 *
 * Deliberately narrow. `/callback` runs `handleRedirectCallback()`, and with rotating
 * refresh tokens a concurrent exchange would race it and can invalidate the token family;
 * `/login`, `/logout*`, `/oauth/authorize` and `/public/*` have no authenticated boot to
 * warm up at all. `/attestation/*` is included because its layout
 * (`routes/~attestation/~route.tsx`) boots through the same `RequireAuth`, which adopts the
 * warm-up, and runs no competing Auth0 exchange; its traffic is almost entirely cold hard
 * loads from email links, so it gains the most from the overlap (CUR-1580). This is
 * separate from, and broader than, the home-only pathname guard `client/index.html` uses for
 * the home image preload, which must stay as it is.
 */
const WARMUP_PATHNAME_PATTERN = /^\/(console|admin-console|attestation)(\/|$)/;

let bootTokenWarmup: Promise<string> | null = null;

/**
 * Starts the Auth0 silent refresh that `RequireAuth` would otherwise only issue once its
 * lazily loaded chunk has downloaded and the tree has mounted — 1.2–1.5 s into a cold load,
 * with the network idle for most of it. Everything needed to decide whether a refresh is
 * required (the persisted access token, its expiry and the persisted user) is already in
 * `localStorage` when the entry module runs, so the exchange can overlap JS boot instead of
 * following it. See CUR-1459.
 *
 * Intentionally free of store side effects: it does not `setAuthToken` or `reloadUser`.
 * `RequireAuth` still owns the state transition and the error UI, which is what keeps this
 * purely a timing change.
 */
export function startBootTokenWarmup(): void {
    if (typeof window === 'undefined' || !WARMUP_PATHNAME_PATTERN.test(window.location.pathname)) {
        return;
    }

    const state = useAuthStore.getState();

    // Exactly the conditions under which RequireAuth falls through to `getAccessToken`:
    // a persisted token that is too close to expiry to render on, plus the persisted
    // user/org that `reloadUser` needs straight afterwards.
    if (!state.isTokenSet()) return;
    if (state.hasValidToken({ minValidityMs: BOOT_TOKEN_MIN_VALIDITY_MS })) return;
    if (!state.user?.id || !state.user?.orgId) return;

    const warmup = state.getAccessToken({
        minValidityMs: TokenRefreshManager.REFRESH_BUFFER_MS,
        // RequireAuth's catch owns error presentation (`message.destroy()`,
        // `AuthErrorFallback`, `redirectToLogin()`), so the warm-up must not pop its
        // own toast or schedule a competing redirect.
        silent: true,
    });

    warmup.catch(() => {
        // `silent` suppresses the toast and `redirectToLogin`, but `handleAuthError` still
        // schedules its own 1 s fallback navigation for any session error (missing/invalid
        // refresh token). That fallback builds `redirectTo` from pathname + search only, so
        // if it fired before the lazily loaded RequireAuth chunk mounted it would navigate
        // early and drop the fragment (`#whats-new/...`). Starting this exchange before the
        // tree exists must not change who owns navigation: cancel the fallback and let the
        // consuming RequireAuth catch redirect, which preserves the full protected URL.
        // This also keeps the rejection off the unhandled-rejection path when nobody
        // consumes the warm-up.
        cancelScheduledLoginRedirect();
    });

    bootTokenWarmup = warmup;
}

/**
 * Hands the in-flight boot refresh to the first caller (`RequireAuth`) and clears it, so a
 * later remount re-reads the store rather than reusing a stale token promise. Returns null
 * when no warm-up was started — callers fall back to their own `getAccessToken`, which
 * dedupes onto any exchange already in flight.
 */
export function consumeBootTokenWarmup(): Promise<string> | null {
    const warmup = bootTokenWarmup;
    bootTokenWarmup = null;
    return warmup;
}

/**
 * Drops a warm-up nobody is going to adopt, without awaiting it.
 *
 * `RequireAuth` calls this when it renders straight off a still-valid token: another tab can
 * publish a fresh token between `startBootTokenWarmup()` and the first auth pass (see
 * `crossTabTokenSync`), so the warm-up's result is never consumed on that pass. Holding it
 * would hand a later remount — one that only runs because renewal is now required — a token
 * minted at boot and possibly already expired, or replay a stale rejection. Clearing it makes
 * that remount issue a current request instead.
 *
 * Safe to call on an in-flight exchange: `startBootTokenWarmup` already attached the
 * rejection handler, so dropping the reference cannot surface an unhandled rejection, and the
 * exchange itself is harmless (auth0-spa-js caches the rotated token).
 */
export function discardBootTokenWarmup(): void {
    bootTokenWarmup = null;
}
